import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';

import { Collapsible, pageStorageKey } from '@/components/Collapsible';

/**
 * Phone treatment for a toolbar's secondary controls (owner, iPhone 15:
 * "things take a lot of swiping left and right").
 *
 * The first mobile pass turned every filter toolbar into a sideways scroll
 * strip. That fixed the five-row stacks, but it moved the cost into the
 * owner's thumb: every select and chip past the screen edge had to be swiped
 * to. This is the pattern phones actually use instead — the search box and
 * the primary action stay visible, and everything secondary sits behind one
 * "Filters" button that opens DOWNWARD, full width, one control per line.
 *
 * Now the "Filters" preset of the shared `Collapsible` (2026-09-23), which
 * brought three things with it: the fold also applies to a phone turned
 * sideways, it remembers open/shut per page, and it is the same control as
 * every other fold in the app. On a desktop it is still invisible — no
 * button, and the children render through `display: contents`, so the
 * toolbar is exactly the flex row it always was.
 *
 * `badge` tells a cashier that a closed disclosure is hiding an ACTIVE
 * filter — without it, "why is the list short" has an invisible answer.
 * `storageKey` defaults to the page's own path.
 */
export function FilterDisclosure({
  children,
  badge = 0,
  storageKey,
}: {
  children: ReactNode;
  badge?: number;
  storageKey?: string;
}) {
  const { t } = useTranslation();
  const [key] = useState(() => storageKey ?? pageStorageKey('filters'));

  return (
    <Collapsible
      label={t('common.filters')}
      icon={SlidersHorizontal}
      count={badge}
      storageKey={key}
      layout="stack"
    >
      {children}
    </Collapsible>
  );
}
